import { Koi } from '@ksyos/koi';
import type { Section } from '@common/formEngine/formEngine.types';
import type { NullablePrimitives } from '@common/utils/utilityTypes';
import type {
    ExternalReferralContentKeys,
    ExternalReferralData,
} from '@common/validation/customControls/externalReferral';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import { AssetCategory } from '@common/enums';
import { dependsOn } from '@common/formEngine/dependsOn';
import { createExternalReferralSubsections } from '@common/validation/customControls/externalReferral';
import { RedenVerwijzingAudioloog } from '@common/validation/customControls/hearingLoss/utils';
import { getTextInputStringSchema } from '@common/validation/utils/getTextInputStringSchema';

export enum YesNoNotApplicable {
    no = 'Nee',
    not_applicable = 'Niet van toepassing',
    yes = 'Ja',
}

const yesNoNotApplicableOptions: YesNoNotApplicable[] = [
    YesNoNotApplicable.yes,
    YesNoNotApplicable.no,
    YesNoNotApplicable.not_applicable,
];

export enum CategorieHoortoestel {
    buitencategorie = 'Buitencategorie',
    categorie1 = 'Categorie 1',
    categorie2 = 'Categorie 2',
    categorie3 = 'Categorie 3',
    categorie4 = 'Categorie 4',
    categorie5 = 'Categorie 5',
}
const categorieHoortoestelOptions: CategorieHoortoestel[] = [
    CategorieHoortoestel.categorie1,
    CategorieHoortoestel.categorie2,
    CategorieHoortoestel.categorie3,
    CategorieHoortoestel.categorie4,
    CategorieHoortoestel.categorie5,
    CategorieHoortoestel.buitencategorie,
];

export enum TypeHoorhulpmiddel {
    anders = 'Anders',
    soloapparatuur = 'Soloapparatuur',
    wek_en_waarschuwingssysteem = 'Wek- en waarschuwingssysteem',
}
const typeHoorhulpmiddelOptions: TypeHoorhulpmiddel[] = [
    TypeHoorhulpmiddel.soloapparatuur,
    TypeHoorhulpmiddel.wek_en_waarschuwingssysteem,
    TypeHoorhulpmiddel.anders,
];

export type HearingLossAudiologistConsultDataBase = NullablePrimitives<{
    advies: string;
    akkoord_hoorhulpmiddel: YesNoNotApplicable;
    antwoord_of_opmerkingen: string;
    assets: ProcessActivityAsset[];
    categorie_hoortoestel: CategorieHoortoestel;
    doorverwijzing_brief: never[];
    opmerkingen_recept: string;
    proef_starten: YesNoNotApplicable;
    recipe?: {
        assetId: string;
        filename: string;
    };
    reden_recept_anders: string;
    reden_recept_soloapparatuur: string;
    reden_recept_wek_en_waarschuwingssysteem: string;
    reden_reden_verwijzing_anders: string;
    reden_specificatie_hoortoestel: string;
    reden_verwijzing: string;
    samenvatting_gesprek: string;
    specificatie_hoortoestel_toelichting: string;
    toelichting_doorverwijzing: string;
    toelichting_recept: string;
    type_hoorhulpmiddel: NullablePrimitives<{
        anders: string;
        soloapparatuur: boolean;
        wek_en_waarschuwingssysteem: boolean;
    }>;
    type_hoortoestel: string;
    verwijzing_specialist_nodig: boolean;
}> &
    ExternalReferralData;

export const hearingLossAudiologistConsultSectionsBase: Section<
    HearingLossAudiologistConsultDataBase,
    ExternalReferralContentKeys
>[] = [
    {
        fields: [
            {
                draftSchema: Koi.koi().enum(RedenVerwijzingAudioloog).allow(null),
                finalSchema: Koi.koi().enum(RedenVerwijzingAudioloog),
                initialValue: null,
                key: 'reden_verwijzing',
                label: 'Reden van verwijzing',
                type: 'custom',
            },
            {
                draftSchema: Koi.when('reden_verwijzing', {
                    is: RedenVerwijzingAudioloog.anders,
                    otherwise: null,
                    then: getTextInputStringSchema(),
                }),
                finalSchema: Koi.when('reden_verwijzing', {
                    is: RedenVerwijzingAudioloog.anders,
                    otherwise: null,
                    then: getTextInputStringSchema(),
                }),
                initialValue: null,
                key: 'reden_reden_verwijzing_anders',
                label: 'Reden anders',
                type: 'custom',
            },
        ],
        title: 'Specificatie soort consult',
    },
    {
        fields: [
            {
                isRequired: true,
                key: 'samenvatting_gesprek',
                label: 'Samenvatting gesprek',
                type: 'textArea',
            },
            {
                isRequired: true,
                key: 'advies',
                label: 'Advies',
                type: 'textArea',
            },
        ],
        title: 'Contact met patiënt',
    },
    {
        fields: [
            {
                isRequired: true,
                key: 'proef_starten',
                label: 'Proef starten',
                layout: 'row',
                type: 'radioGroup',
                valueOptions: yesNoNotApplicableOptions,
            },
            {
                include: dependsOn('proef_starten', ({ proef_starten }) => proef_starten === YesNoNotApplicable.yes),
                isRequired: true,
                key: 'categorie_hoortoestel',
                label: 'Categorie hoortoestel',
                type: 'radioGroup',
                valueOptions: categorieHoortoestelOptions,
            },
            {
                include: dependsOn('proef_starten', ({ proef_starten }) => proef_starten === YesNoNotApplicable.yes),
                isRequired: true,
                key: 'type_hoortoestel',
                label: 'Type hoortoestel',
                type: 'textInput',
            },
            {
                include: dependsOn('proef_starten', ({ proef_starten }) => proef_starten === YesNoNotApplicable.yes),
                isRequired: true,
                key: 'reden_specificatie_hoortoestel',
                label: 'Reden',
                type: 'textArea',
            },
            {
                include: dependsOn('proef_starten', ({ proef_starten }) => proef_starten === YesNoNotApplicable.no),
                isRequired: true,
                key: 'specificatie_hoortoestel_toelichting',
                label: 'Toelichting',
                type: 'textArea',
            },
        ],
        title: 'Specificatie hoortoestel',
    },
    {
        fields: [
            {
                isRequired: true,
                key: 'akkoord_hoorhulpmiddel',
                label: 'Akkoord hoorhulpmiddel',
                layout: 'row',
                type: 'radioGroup',
                valueOptions: yesNoNotApplicableOptions,
            },
            {
                checkboxEnumType: TypeHoorhulpmiddel,
                include: dependsOn(
                    'akkoord_hoorhulpmiddel',
                    ({ akkoord_hoorhulpmiddel }) => akkoord_hoorhulpmiddel === YesNoNotApplicable.yes
                ),
                isRequired: true,
                key: 'type_hoorhulpmiddel',
                label: 'Type hoorhulpmiddel',
                type: 'checkboxGroup',
                valueOptions: typeHoorhulpmiddelOptions,
            },
            {
                draftSchema: Koi.when('type_hoorhulpmiddel.soloapparatuur', {
                    is: true,
                    otherwise: null,
                    then: getTextInputStringSchema().allow(null),
                }),
                finalSchema: Koi.when('type_hoorhulpmiddel.soloapparatuur', {
                    is: true,
                    otherwise: null,
                    then: getTextInputStringSchema(),
                }),
                include: dependsOn(
                    'type_hoorhulpmiddel',
                    ({ type_hoorhulpmiddel }) => !!type_hoorhulpmiddel.soloapparatuur
                ),
                initialValue: null,
                key: 'reden_recept_soloapparatuur',
                label: 'Reden recept soloapparatuur',
                type: 'custom',
            },
            {
                draftSchema: Koi.when('type_hoorhulpmiddel.wek_en_waarschuwingssysteem', {
                    is: true,
                    otherwise: null,
                    then: getTextInputStringSchema().allow(null),
                }),
                finalSchema: Koi.when('type_hoorhulpmiddel.wek_en_waarschuwingssysteem', {
                    is: true,
                    otherwise: null,
                    then: getTextInputStringSchema(),
                }),
                include: dependsOn(
                    'type_hoorhulpmiddel',
                    ({ type_hoorhulpmiddel }) => !!type_hoorhulpmiddel.wek_en_waarschuwingssysteem
                ),
                initialValue: null,
                key: 'reden_recept_wek_en_waarschuwingssysteem',
                label: 'Reden recept wek- en waarschuwingssysteem',
                type: 'custom',
            },
            {
                draftSchema: Koi.when('type_hoorhulpmiddel.anders', {
                    is: Koi.string(),
                    otherwise: null,
                    then: getTextInputStringSchema().allow(null),
                }),
                finalSchema: Koi.when('type_hoorhulpmiddel.anders', {
                    is: Koi.string(),
                    otherwise: null,
                    then: getTextInputStringSchema(),
                }),
                include: dependsOn('type_hoorhulpmiddel', ({ type_hoorhulpmiddel }) => !!type_hoorhulpmiddel.anders),
                isRequired: true,
                key: 'reden_recept_anders',
                label: 'Reden recept anders',
                type: 'textArea',
            },
            {
                include: dependsOn(
                    'akkoord_hoorhulpmiddel',
                    ({ akkoord_hoorhulpmiddel }) => akkoord_hoorhulpmiddel === YesNoNotApplicable.no
                ),
                isRequired: true,
                key: 'toelichting_recept',
                label: 'Toelichting',
                type: 'textArea',
            },
            {
                include: dependsOn(
                    'akkoord_hoorhulpmiddel',
                    ({ akkoord_hoorhulpmiddel }) => akkoord_hoorhulpmiddel === YesNoNotApplicable.yes
                ),
                key: 'opmerkingen_recept',
                label: 'Opmerkingen recept',
                type: 'textArea',
            },
        ],
        title: 'Specificatie recept',
    },
    {
        fields: [
            {
                isRequired: true,
                key: 'verwijzing_specialist_nodig',
                label: 'Verwijzing naar specialist nodig',
                type: 'boolean',
            },
        ],
        subsections: createExternalReferralSubsections<HearingLossAudiologistConsultDataBase>(
            dependsOn('verwijzing_specialist_nodig')
        ),
        title: 'Doorverwijzing',
    },
    {
        fields: [
            {
                key: 'antwoord_of_opmerkingen',
                label: 'Antwoorden of opmerkingen',
                type: 'textArea',
            },
        ],
        title: 'Bericht aan audicien',
    },
    {
        fields: [
            {
                category: AssetCategory.recipe,
                draftSchema: Koi.optional(),
                finalSchema: Koi.optional(),
                initialValue: undefined,
                isReadonly: true,
                key: 'recipe',
                label: 'Recept',
                type: 'assets',
            },
            {
                category: AssetCategory.externalReferralLetter,
                draftSchema: Koi.optional(),
                finalSchema: Koi.optional(),
                isReadonly: true,
                key: 'doorverwijzing_brief',
                label: 'Doorverwijzing',
                type: 'assets',
            },
        ],
        title: 'Bijlages',
    },
];
